import { PaymentDeviceFeeChargeStatus } from '@/models/common/PaymentDeviceEnums';

/**
 * The card-terminal rental fee (HAT-1434, part 3): 7.5 ILS per business day from the hand-over to
 * the return, recorded in APT on the 1st of each month for the month before. The server computes
 * every amount; these helpers only label and format what it sends.
 */

export const MANAGEMENT_DEVICE_FEES_URL = 'management/payment-device-fees';

export const managementDeviceFeesUrl = (month?: string | null) =>
  month ? `${MANAGEMENT_DEVICE_FEES_URL}?month=${month}` : MANAGEMENT_DEVICE_FEES_URL;

/** The colour family a charge status is shown in - the same tones as the coordinator page's pills. */
export type FeeTone = 'neutral' | 'success' | 'danger' | 'warning' | 'info';

export const feeChargeTone = (status: PaymentDeviceFeeChargeStatus): FeeTone => {
  switch (status) {
    case PaymentDeviceFeeChargeStatus.Written:
      return 'success';
    case PaymentDeviceFeeChargeStatus.Simulated:
      return 'info';
    case PaymentDeviceFeeChargeStatus.Failed:
      return 'danger';
    case PaymentDeviceFeeChargeStatus.Skipped:
      return 'warning';
    default:
      return 'neutral';
  }
};

/** Who reads the fee: the office speaks of APT, the coordinator of what was paid. */
export type FeeAudience = 'office' | 'coordinator';

export const feeChargeStatusKey = (status: PaymentDeviceFeeChargeStatus, audience: FeeAudience = 'office') =>
  audience === 'coordinator' ? `paymentDevices.fees.coordinatorStatus.${status}` : `paymentDevices.fees.status.${status}`;

/** Amounts in shekels with exactly two decimals, sign after the number as elsewhere on the site: "97.50 ₪". */
export const formatFeeAmount = (amount: number): string =>
  `${amount.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₪`;

const HEBREW_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

/** "2026-10-01" → "אוקטובר 2026", read from the string so no time zone can move it to September. */
export const formatFeeMonth = (chargeMonth: string | null | undefined): string => {
  if (!chargeMonth || chargeMonth.length < 7) return '';
  const year = chargeMonth.slice(0, 4);
  const month = Number(chargeMonth.slice(5, 7));
  const name = HEBREW_MONTHS.at(month - 1);
  return name ? `${name} ${year}` : '';
};

/** The 1st of the month after `today` ("YYYY-MM-DD") - when what is pending now gets recorded in APT. */
export const nextChargeDate = (today: string): string => {
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  const nextYear = month === 12 ? year + 1 : year;
  const nextMonth = month === 12 ? 1 : month + 1;
  return `${nextYear}-${String(nextMonth).padStart(2, '0')}-01`;
};

/** The total of a month's charges, for the line above the office's table. */
export const sumFeeCharges = (charges: { amount: number }[]): number =>
  Math.round(charges.reduce((sum, charge) => sum + charge.amount, 0) * 100) / 100;
